import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  styled,
  Collapse,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import SummarizeIcon from '@mui/icons-material/Summarize';
import LogoutIcon from '@mui/icons-material/Logout';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import MapIcon from '@mui/icons-material/Map';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SyncIcon from '@mui/icons-material/Sync';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { useOfflineData } from '../hooks/useOfflineData';


const drawerWidth = 240;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  zIndex: theme.zIndex.drawer + 1,
}));

const AppTitle = () => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    height: '64px',
    padding: '0 16px',
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    width: '100%'
  }}>
    <Typography
      variant="h6"
      noWrap
      component="div"
      sx={{
        fontFamily: 'monospace',
        fontWeight: 700,
        letterSpacing: '.2rem',
        color: 'white'
      }}
    >
      ANSAB MEAL
    </Typography>
  </Box>
);

export default function NavBar({ window }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isOnline, pendingCount, isSyncing, syncOfflineInventories } = useOfflineData();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({
    individual: false,
    enterprise: false,
    fug: false,
    settings: false,
    inventory: false,
    projects: false
  });
  const [anchorEl, setAnchorEl] = useState(null);

  // Auto-expand menu if current path is in subitems
  useEffect(() => {
    const path = location.pathname;
    const newExpandedMenus = { ...expandedMenus };

    if (path.includes('/individuals') || path.includes('/add-individual')) {
      newExpandedMenus.individual = true;
    }
    if (path.includes('/enterprise') || path.includes('/add-enterprise')) {
      newExpandedMenus.enterprise = true;
    }
    if (path.includes('/fug') || path.includes('/fuglist')) {
      newExpandedMenus.fug = true;
    }
    if (path.includes('/settings') || path.includes('/users')) {
      newExpandedMenus.settings = true;
    }
    if (path.includes('/inventory') || path.includes('/inventory-plot')) {
      newExpandedMenus.inventory = true;
    }
    if (path.includes('/projects') || path.includes('/add-project')) {
      newExpandedMenus.projects = true;
    }

    setExpandedMenus(newExpandedMenus);
  }, [location]);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleMenuClick = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const handleLogoutClick = () => {
    setLogoutConfirmOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLogoutConfirmOpen(false);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutConfirmOpen(false);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleUserMenuClose();
    navigate('/profile');
  };

  const getDrawerItems = () => {
    // For temp-staff, show Home and Inventory menus (no SDM)
    if (user?.role === 'temp-staff') {
      return [
        { label: 'Home', icon: <HomeIcon color="primary" />, to: '/' },
        {
          label: 'Inventory',
          icon: <InventoryIcon color="primary" />,
          key: 'inventory',
          subItems: [
            { label: 'View Inventory', icon: <CategoryIcon color="primary" />, to: '/inventory' },
            { label: 'View Plot', icon: <MapIcon color="primary" />, to: '/inventory-plots' },
            { label: 'Stories', icon: <AutoStoriesIcon color="primary" />, to: '/inventory-stories' },
            { label: 'Planning', icon: <CategoryIcon color="primary" />, to: '/inventory-plan-list' },
            { label: 'Add Inventory', icon: <AddIcon color="primary" />, to: '/inventory-add' },
            // SDM only for admin
            ...(user?.role === 'admin' ? [{ label: 'SDM', icon: <ShowChartIcon color="primary" />, to: '/sdm' }] : []),
          ]
        }
      ];

    }

    // Get individual subitems based on user role
    const getIndividualSubItems = () => {
      const baseItems = [
        { label: 'View Individuals', icon: <PeopleIcon color="primary" />, to: '/individuals' },
        { label: 'View LRPs', icon: <PeopleIcon color="primary" />, to: '/lrp-list' },
        { label: 'Individual Reports', icon: <AssessmentIcon color="primary" />, to: '/individuals/report' },
        { label: 'Add Individual', icon: <AddIcon color="primary" />, to: '/add-individual' }
      ];

      if (user?.role === 'admin') {
        baseItems.splice(2, 0, { label: 'Data Analysis', icon: <BarChartIcon color="primary" />, to: '/individuals/analysis' });
      }

      return baseItems;
    };

    const baseItems = [
      { label: 'Home', icon: <HomeIcon color="primary" />, to: '/' },
      {
        label: 'Projects',
        icon: <AccountTreeIcon color="primary" />,
        key: 'projects',
        subItems: [
          { label: 'View Projects', icon: <VisibilityIcon color="primary" />, to: '/projects' },
          ...(user?.role === 'admin' || user?.role === 'manager' ?
            [{ label: 'Add Project', icon: <AddIcon color="primary" />, to: '/add-project' }] :
            [])
        ]
      },
      {
        label: 'Individual',
        icon: <PersonIcon color="primary" />,
        key: 'individual',
        subItems: getIndividualSubItems()
      },
      {
        label: 'Enterprise',
        icon: <BusinessIcon color="primary" />,
        key: 'enterprise',
        subItems: [
          { label: 'View Enterprises', icon: <BusinessIcon color="primary" />, to: '/enterprise' },
          { label: 'Enterprise Reports', icon: <BarChartIcon color="primary" />, to: '/enterprise/report' },
          { label: 'Add Enterprise', icon: <AddIcon color="primary" />, to: '/add-enterprise' }
        ]
      },
      {
        label: 'Group',
        icon: <GroupIcon color="primary" />,
        key: 'fug',
        subItems: [
          { label: 'View Groups', icon: <HandshakeIcon color="primary" />, to: '/fuglist' },
          { label: 'Group Reports', icon: <PieChartIcon color="primary" />, to: '/fuglist/report' },
          { label: 'Add Group', icon: <AddIcon color="primary" />, to: '/fug' }
        ]
      },
      {
        label: 'Inventory',
        icon: <InventoryIcon color="primary" />,
        key: 'inventory',
        subItems: [
          { label: 'View Inventory', icon: <CategoryIcon color="primary" />, to: '/inventory' },
          { label: 'View Plot', icon: <MapIcon color="primary" />, to: '/inventory-plots' },
          { label: 'Stories', icon: <AutoStoriesIcon color="primary" />, to: '/inventory-stories' },
          { label: 'Planning', icon: <CategoryIcon color="primary" />, to: '/inventory-plan-list' },
          { label: 'Inventory Reports', icon: <AssessmentIcon color="primary" />, to: '/inventory/report' },
          { label: 'Add Inventory', icon: <AddIcon color="primary" />, to: '/inventory-add' },
          // SDM only for admin
          ...(user?.role === 'admin' ? [{ label: 'SDM', icon: <ShowChartIcon color="primary" />, to: '/sdm' }] : []),
        ]
      },
      { label: 'Plannings', icon: <SummarizeIcon color="primary" />, to: '/planning/list' },
      { label: 'All Reports', icon: <SummarizeIcon color="primary" />, to: '/reports' },
      { label: 'User Contributions', icon: <ReceiptIcon color="primary" />, to: '/user-contributions' }
    ];

    if (user?.role === 'admin' || user?.role === 'manager' || user?.role === 'coordinator') {
      baseItems.push({ label: 'Hall Booking', icon: <MeetingRoomIcon color="primary" />, to: '/hall-booking' });
    }

    if (user?.role === 'admin' || user?.role === 'manager' || user?.role === 'coordinator') {
      baseItems.push({
        label: 'Settings',
        icon: <SettingsIcon color="primary" />,
        key: 'settings',
        subItems: [
          { label: 'Project Settings', icon: <AccountTreeIcon color="primary" />, to: '/settings/project' },
          { label: 'Event Management', icon: <EventIcon color="primary" />, to: '/settings/events' },
          { label: 'Beneficiary Types', icon: <PersonIcon color="primary" />, to: '/settings/beneficiaries' },
          ...(user?.role === 'admin' ? [{ label: 'User Management', icon: <ManageAccountsIcon color="primary" />, to: '/users' }] : [])
        ]
      });
    }

    return baseItems;
  };

  const isSubItemActive = (subItems) => {
    return subItems?.some(subItem => location.pathname === subItem.to);
  };

  const getCurrentPageTitle = () => {
    if (location.pathname === '/') return 'Home';

    const items = getDrawerItems();
    for (const item of items) {
      if (item.subItems) {
        const activeSubItem = item.subItems.find(subItem => subItem.to === location.pathname);
        if (activeSubItem) return activeSubItem.label;
      } else if (item.to === location.pathname) {
        return item.label;
      }
    }
    return 'MEAL Dashboard';
  };

  const drawer = (
    <Box sx={{ overflow: 'auto' }}>
      <AppTitle />
      <List sx={{ p: 1 }}>
        {getDrawerItems().map((item) => {
          if (item.subItems) {
            return (
              <React.Fragment key={item.key}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleMenuClick(item.key)}
                    selected={isSubItemActive(item.subItems)}
                    sx={{
                      borderRadius: 1,
                      margin: '0 8px',
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                        '&:hover': { backgroundColor: 'action.selected' }
                      },
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        variant: 'body1',
                        color: 'text.primary',
                        fontWeight: isSubItemActive(item.subItems) ? 600 : 400
                      }}
                    />
                    {expandedMenus[item.key] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={expandedMenus[item.key]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem key={subItem.label} disablePadding>
                        <ListItemButton
                          component={Link}
                          to={subItem.to}
                          selected={location.pathname === subItem.to}
                          sx={{
                            borderRadius: 1,
                            margin: '0 16px',
                            pl: 4,
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': { backgroundColor: 'primary.dark' },
                              '& .MuiListItemIcon-root': { color: 'white !important' },
                              '& .MuiSvgIcon-root': { color: 'white !important' }
                            },
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>{subItem.icon}</ListItemIcon>
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: location.pathname === subItem.to ? 'inherit' : 'text.secondary'
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          } else {
            return (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.to}
                  selected={location.pathname === item.to}
                  sx={{
                    borderRadius: 1,
                    margin: '0 8px',
                    '&.Mui-selected': {
                      backgroundColor: 'action.selected',
                      '&:hover': { backgroundColor: 'action.selected' }
                    },
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'body1',
                      color: 'text.primary'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          }
        })}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <StyledAppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {getCurrentPageTitle()}
          </Typography>
          {/* Offline / Sync Indicators */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
            {!isOnline && (
              <Tooltip title="You are currently offline. Forms will be saved locally.">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: 'error.main',
                    color: 'white',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  <WifiOffIcon sx={{ fontSize: 16 }} />
                  Offline
                </Box>
              </Tooltip>
            )}

            {pendingCount > 0 && (
              <Tooltip title={isOnline ? `${pendingCount} inventories pending sync. Click to sync now.` : `${pendingCount} inventories saved offline.`}>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  disabled={isSyncing || !isOnline}
                  onClick={syncOfflineInventories}
                  startIcon={<SyncIcon sx={{ 
                    fontSize: 16,
                    animation: isSyncing ? 'spin 1.5s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }} />}
                  sx={{
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    py: 0.5,
                    px: 1.5,
                    minHeight: 0,
                    boxShadow: 'none',
                    color: 'warning.contrastText',
                    '&:hover': {
                      boxShadow: 'none',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                    }
                  }}
                >
                  {isSyncing ? 'Syncing...' : `${pendingCount} Pending`}
                </Button>
              </Tooltip>
            )}
          </Box>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="User menu">
                <Avatar
                  onClick={handleUserMenuOpen}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'secondary.main',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
              </Tooltip>
              <Button
                color="inherit"
                onClick={handleUserMenuOpen}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  color: 'white',
                  display: { xs: 'none', sm: 'flex' },
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
                title={`Logged in as ${user.full_name} (${user.role})`}
              >
                {user.full_name}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                  My Profile
                </MenuItem>
                <MenuItem onClick={handleLogoutClick}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </StyledAppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="navigation menu">
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          onTransitionEnd={handleDrawerTransitionEnd}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, boxShadow: 3 }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none', backgroundColor: 'background.paper' }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      <Dialog open={logoutConfirmOpen} onClose={handleLogoutCancel} aria-labelledby="logout-dialog-title">
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to logout?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="primary">Cancel</Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained" startIcon={<LogoutIcon />}>Logout</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}